import { useMutation, useLazyQuery } from '@apollo/client';
import TicketScheduleModal from '@nile/banyan/src/Containers/SheetContainers/TicketScheduleModal';
import { GET_AI_FEATURES_LIST_INDIVIDUAL } from '@nile/banyan/src/Graphql/Query/ai';
import {
  AccessControl,
  AI_AGENTS_MAP,
  COMMON_CONSTANTS,
  ENTITY_MAP,
  EventEmitter,
  FeatureGuide,
  getCurrentUserHook,
  getEntityAttributes,
  getTranslatedText,
  Loader,
  newTicketConstraints,
  toast,
  useActivityTracker,
  useCheckFeatureGating,
  useIsAuthorisedHook,
  useProduct,
  useUserMSPCheckHook,
  validateForm
} from '@nile/components';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import WorklogHoursSection from '@nile/components/src/Containers/Worklog/WorklogHoursSection';
import { useChatFeatureEnabled } from '@nile/components/src/Pages/SystemTrayChatPage/utils';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import {
  dompurifier,
  formatFormTemplate
} from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_DEFAULT_TICKET_FORM,
  GET_DEFAULT_TICKET_FORM_NEW,
  GET_ENTITY_PROPERTIES,
  GET_PUBLISHED_TICKET_FORMS,
  GET_PUBLISHED_TICKET_FORMS_NEW,
  MLClient,
  SAVE_TICKET,
  TicketClient,
  useCachedQuery
} from '@nile/graphql';
import { GET_CLIENTS_FOR_DROPDOWN } from '@nile/graphql/src/Mutation/client';
import envConfig from '@nile/home/src/Config/env';
import { removePropertiesForItProduct } from '@nile/itsm';
import { GET_TICKET_FORMS_BY_REQUESTER_GROUP } from '@nile/itsm/src/graphql';
import { DepartmentSelect } from '@nile/itsm/src/pages/components/DepartmentSelect';
import {
  ClientSelect,
  DualCell,
  UserSelect
} from '@nile/mangifera/src/Components/Ticket';
import TicketTypeContainer from '@nile/mangifera/src/Components/Ticket/TicketTypeContainer';
import TicketAssetsSheet from '@nile/mangifera/src/Containers/SheetContainers/TicketAssetsSheet';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/mangifera/src/Pebbles/select';
import {
  getAccessor,
  getCellType,
  getDependantField,
  getParentField,
  isDependantField
} from '@nile/mangifera/src/Pebbles/TicketContainer';
import { useCreateTicketStore } from '@nile/mangifera/src/StoreManager/store/createTicketStore';
import SiteSelect from '@nile/projects/src/Components/Inputs/SiteSelect';
import AssetTypeCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-type-cell';
import { GENERATE_TICKET_DEFLECTION_SUGGESTIONS_V2 } from '@nile/rmm/src/modules/knowledge-base/graphql';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Dropdown,
  Heading,
  Modal,
  Page,
  TextStyle
} from '@superops/ui-design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep, isEmpty, pick } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormattedMessage, useIntl } from 'react-intl';
import { AssociateAssets, Wrapper } from './index';
import KbRelevantArticle from './kbRelevantArticle';
import TicketForm from './TicketForm';

const TechnicianCell = (props) => {
  const { entitiesMeta, ticket, updateProperties, getCellProps } = props;
  const allEntityFields = getEntityAttributes(entitiesMeta, ENTITY_MAP.ticket);
  const field = allEntityFields.find((el) => el.name === 'technician');

  const fieldValue = ticket?.technician || null;

  const defaultValue = fieldValue
    ? {
        ...fieldValue,
        name: `${fieldValue.firstName} ${fieldValue.lastName}`,
        label: `${fieldValue.firstName} ${fieldValue.lastName}`,
        value: `${fieldValue.firstName} ${fieldValue.lastName}`
      }
    : null;
  // const Tag = getTag(field);
  const allProps = getCellProps(field);

  return (
    <div className="flex actr full-height technician-select">
      <Avatar
        size="small"
        customer={!ticket.technician && !ticket.techGroup}
        style="two"
        initials={
          ticket.technician
            ? `${ticket?.technician?.firstName?.[0]}${ticket?.technician?.lastName?.[0]}`
            : ticket?.techGroup?.name?.[0]
        }
      />
      <div className="full-width flex actr">
        <DualCell
          defaultValue={defaultValue}
          updateSelection={updateProperties}
          _ticket={ticket}
          field={field}
          params={allProps}
        />
      </div>
      <div
        className="pointer closeIcon"
        onClick={() => updateProperties({ technician: null })}
        aria-hidden
      >
        <SVGIcon name="Close" size="sm" />
      </div>
    </div>
  );
};

const TicketSheetNew = (props) => {
  const { noRedirect } = props;
  const intl = useIntl();
  const currentProduct = useProduct();
  const currentUser = getCurrentUserHook();
  const homeContext = useContext(HomeContext);
  const tabContext = useContext(TabContext);
  const aiFeatureHook = AIFeaturesHook();

  const ticketDeflectionFeatureData = aiFeatureHook?.getAIFeatureData(
    AI_AGENTS_MAP.TICKET_DEFLECTION
  );
  const isTicketDeflectionActive = ticketDeflectionFeatureData?.isActive;

  const superEditorConfig = {
    id: 'ticketSheetNew',
    name: 'ticketSheetNew'
  };

  const { template, updateTemplate } = useSuperEditor(superEditorConfig);

  const {
    data: entitiesMeta,
    loading: entitiesLoading,
    error: entitiesError
  } = useCachedQuery(GET_ENTITY_PROPERTIES, { client: AccountClient });

  const flags = useFlags();
  const isChatAvailable = useChatFeatureEnabled();
  const runbookRevampFlag = flags?.unifiedRunbooks;
  const requesterGroupsFlag = flags?.requesterGroups;

  const defaultFormQuery = runbookRevampFlag
    ? GET_DEFAULT_TICKET_FORM_NEW
    : GET_DEFAULT_TICKET_FORM;

  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const isMSP = useUserMSPCheckHook();
  const isMSPClient = !isMSP && isMSPProduct;
  const assignedClient = currentUser?.associations?.[0]?.client;
  const clientId = isMSPClient
    ? homeContext?.multiSiteAssociations?.[0]?.client?.accountId
    : currentUser?.associations?.[0]?.client?.accountId;

  const userAssociations = isMSPClient
    ? homeContext?.multiSiteAssociations
    : currentUser?.associations;

  const userDepartment = currentUser?.businessFunction;

  const clientList = userAssociations?.map((c) => {
    return {
      name: c.org.name,
      accountId: c.org.accountId
    };
  });
  const userId = currentUser?.userId;
  const isClient = !isMSP;
  const userRole = currentUser?.role?.roleFeatureList || [];
  const isAssetAuthorised = useIsAuthorisedHook('asset.view');
  const isAssetGatingAvailable = useCheckFeatureGating('WINDOWS_MANAGEMENT')
    ?.enabled;
  const isKnowledgeBaseAvailable = useCheckFeatureGating('KNOWLEDGE_BASE')
    ?.enabled;

  const getDefaultTechnician = () => {
    if (isMSP && currentUser) {
      return {
        userId: currentUser?.userId,
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName
      };
    }
    return null;
  };

  const getClientFromTab = () => {
    if (isMSP) {
      const clientFromTab =
        tabContext?.activeTab?.state?.ticket?.client?.accountId;
      if (clientFromTab) {
        return {
          accountId: clientFromTab
        };
      }
    }
    return null;
  };

  const getDefaultClient = () => {
    if (isClient) {
      const defaultClientId = assignedClient?.accountId || clientId;
      return {
        accountId: defaultClientId,
        name: assignedClient?.name
      };
    }
    const clientFromTab = getClientFromTab();
    return clientFromTab || null;
  };

  const getTicketDataFromAssets = () => {
    const ticketFromTab = tabContext?.activeTab?.state?.ticket;
    const assetsFromTab = ticketFromTab?.addAssets;

    if (
      assetsFromTab &&
      Array.isArray(assetsFromTab) &&
      assetsFromTab.length > 0
    ) {
      const addAssets = assetsFromTab.map((asset: any) => ({
        assetId: asset.assetId,
        assetClass: asset.assetClass,
        name: asset.name
      }));
      const client = ticketFromTab?.client;
      const requester = ticketFromTab?.requester;
      const site = ticketFromTab?.site;
      const department = ticketFromTab?.department;

      const defaultTicketData = {
        addAssets,
        requester,
        site
      };

      if (isMSPProduct) {
        defaultTicketData.client = client;
      } else {
        defaultTicketData.department = department;
      }
      return defaultTicketData;
    }
    return null;
  };

  const [isUploading, setIsUploading] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [sheetActive, setSheetActive] = useState(false);
  const [confirmDeflectionModalOpen, setConfirmDeflectionModalOpen] = useState(
    false
  );
  const [ticketFormOptions, setTicketFormOptions] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);

  const source = useCreateTicketStore((state) => state.source);
  const setSource = useCreateTicketStore((state) => state.setSource);

  const ticket = useCreateTicketStore((state) => state.ticket);
  const setTicket = useCreateTicketStore((state) => state.setTicket);
  const updateTicketProperty = useCreateTicketStore(
    (state) => state.updateTicketProperty
  );

  const selectedFormTemplate = useCreateTicketStore(
    (state) => state.selectedFormTemplate
  );
  const setSelectedFormTemplate = useCreateTicketStore(
    (state) => state.setSelectedFormTemplate
  );

  const errors = useCreateTicketStore((state) => state.errors);
  const setErrors = useCreateTicketStore((state) => state.setErrors);

  const mandatoryFieldsOnCreation = useCreateTicketStore(
    (state) => state.mandatoryFieldsOnCreation
  );
  const setMandatoryFieldsOnCreation = useCreateTicketStore(
    (state) => state.setMandatoryFieldsOnCreation
  );

  const showAdditionalRequester = useCreateTicketStore(
    (state) => state.showAdditionalRequester
  );
  const setShowAdditionalRequester = useCreateTicketStore(
    (state) => state.setShowAdditionalRequester
  );

  const suggestions = useCreateTicketStore((state) => state.suggestions);
  const setSuggestions = useCreateTicketStore((state) => state.setSuggestions);

  const ticketDeflectionClientConfigType = useCreateTicketStore(
    (state) => state.ticketDeflectionClientConfigType
  );
  const setTicketDeflectionClientConfigType = useCreateTicketStore(
    (state) => state.setTicketDeflectionClientConfigType
  );

  const ticketDeflectionClientList = useCreateTicketStore(
    (state) => state.ticketDeflectionClientList
  );
  const setTicketDeflectionClientList = useCreateTicketStore(
    (state) => state.setTicketDeflectionClientList
  );

  const checkClient = isClient;

  const associateTimer = tabContext?.activeTab?.data?.associateTimer;
  const timerRef = React.useRef(null);

  const initialized = useCreateTicketStore((state) => state.initialized);
  const setInitialized = useCreateTicketStore((state) => state.setInitialized);

  const getTicketFormsQuery = () => {
    const hasRequester = Boolean(ticket?.requester?.userId);

    /** Get the Ticket Forms by Requester Group */
    if (requesterGroupsFlag && !isMSPProduct && hasRequester) {
      return GET_TICKET_FORMS_BY_REQUESTER_GROUP;
    }
    if (runbookRevampFlag) {
      return GET_PUBLISHED_TICKET_FORMS_NEW;
    }
    return GET_PUBLISHED_TICKET_FORMS;
  };

  const getFormsPayload = (data) => {
    const hasRequester = Boolean(ticket?.requester?.userId);

    if (requesterGroupsFlag && !isMSPProduct && hasRequester) {
      return data?.getTicketFormsByRequesterGroup || [];
    }
    if (runbookRevampFlag) {
      return data?.getPublishedTicketForms || [];
    }
    return data?.getPublishedTicketForms || [];
  };

  const getTicketForms = async () => {
    if (!isEmpty(ticketFormOptions)) {
      return;
    }
    setFormsLoading(true);
    const query = getTicketFormsQuery();
    const variables =
      isMSPProduct && requesterGroupsFlag
        ? {}
        : {
            input: {
              user: {
                userId: ticket?.requester?.userId
              }
            }
          };

    try {
      const response = await TicketClient.query({
        query,
        variables
      });

      const formsData = getFormsPayload(response?.data);
      const formOptionsData = formsData.map((el) => {
        return {
          ...el,
          value: el.formName,
          label: el.formName,
          name: el.formName
        };
      });
      setTicketFormOptions(formOptionsData);
    } catch (error) {
      // console.log(error);
    } finally {
      setFormsLoading(false);
    }
  };

  const getSiteByOrgAccountId = (accountId = null) => {
    const currentClientId = accountId || ticket?.client.accountId;
    const assingedSite = userAssociations.find(
      (item) => item?.org?.accountId === currentClientId
    );
    return assingedSite ? [assingedSite.site] : null;
  };

  const getNestedChilds = (currentField, meta) => {
    const nestedChild = [];
    if (currentField?.nestedAttributes) {
      const getChilds = (fld) => {
        fld?.nestedAttributes?.map((el) => {
          const child = meta?.attributes?.find((attr) => attr?.name === el);
          if (child) {
            nestedChild.push(child);
            if (child?.nestedAttributes) {
              getChilds(child);
            }
          }
        });
      };
      getChilds(currentField);
    }
    return nestedChild;
  };

  const propertiesToAppendMap = {
    client: {
      requester: null,
      addAdditionalRequester: null,
      site: null
    }
  };

  const updateDepedentFormField = (key, field, additionalProps = null) => {
    const ticketFields = entitiesMeta?.getEntitiesMeta?.find(
      (el) => el?.name === 'Ticket'
    );
    const currentFld = ticketFields?.attributes?.find(
      (attr) => attr?.name === key
    );
    const req = ticket?.requester ? { ...ticket?.requester } : null;
    let propertiesToAppend = {};
    let customFields = ticket?.customFields ? { ...ticket?.customFields } : {};
    let updatedFieldLayout = selectedFormTemplate
      ? JSON.parse(selectedFormTemplate?.fieldDetails)?.layout
      : [];

    const defaultPropertiesToAppend = propertiesToAppendMap?.[key];
    if (defaultPropertiesToAppend) {
      propertiesToAppend = {
        ...propertiesToAppend,
        ...defaultPropertiesToAppend
      };
    }
    if (!isEmpty(currentFld?.nestedAttributes)) {
      const resetFldValue = getNestedChilds(currentFld, ticketFields);
      if (!isEmpty(resetFldValue)) {
        const customFldReset = resetFldValue?.filter((el) => el?.isCustom);
        updatedFieldLayout = updatedFieldLayout?.map((el) => ({
          ...el,
          field: {
            ...el?.field,
            fieldProps: resetFldValue.some(
              (f) => f.name === el?.field?.fieldProps?.name
            )
              ? {
                  ...el?.field?.fieldProps,
                  defaultValue: null,
                  selectedValue: null
                }
              : el?.field?.fieldProps
          }
        }));

        if (!isEmpty(customFldReset)) {
          customFldReset?.forEach((el) => {
            customFields = { ...customFields, [el?.name]: null };
          });
          propertiesToAppend = {
            ...propertiesToAppend,
            customFields
          };
        }

        const defaultFldReset = resetFldValue?.filter((el) => !el?.isCustom);
        defaultFldReset?.forEach((el) => {
          propertiesToAppend = { ...propertiesToAppend, [el?.name]: null };
        });
      }
    }
    if (key === 'client' && isClient) {
      const site = getSiteByOrgAccountId(field?.accountId);
      if (site?.length > 0) {
        propertiesToAppend = {
          ...propertiesToAppend,
          site: site[0]
        };
      }
    }
    if (additionalProps && additionalProps?.additionalFields) {
      const updatedTicket = {
        ...propertiesToAppend,
        [key]: field,
        ...additionalProps
      };
      updateTicketProperty(updatedTicket);

      if (!isEmpty(updatedFieldLayout)) {
        const currentFormTemplate = selectedFormTemplate;
        if (currentFormTemplate) {
          setSelectedFormTemplate({
            ...currentFormTemplate,
            fieldDetails: JSON.stringify({ layout: updatedFieldLayout })
          });
        }
      }
      return;
    }
    const updatedTicket = {
      ...propertiesToAppend,
      [key]: field
    };
    updateTicketProperty(updatedTicket);
  };

  const updateField = (key, field, additionalProps = null) => {
    updateDepedentFormField(key, field, additionalProps);
  };

  const updateSiteUser = (siteUserData) => {
    for (let siteuser in siteUserData) {
      updateField(siteuser, siteUserData[siteuser]);
    }
  };

  const clearTechnician = () => {
    const updatedTicket = cloneDeep(ticket);
    delete updatedTicket.technician;
    setTicket(updatedTicket);
  };

  const updateProperties = (properties) => {
    const updatedTicket = { ...ticket, ...properties };
    setTicket(updatedTicket);
  };

  const getCellProps = (col) => {
    if (col) {
      const allEntityFields = getEntityAttributes(
        entitiesMeta,
        ENTITY_MAP.ticket
      );
      return {
        type: getCellType(col),
        accessor: getAccessor(col),
        editable: true,
        actions: { save: updateProperties },
        field: col,
        parentField: isDependantField(col.name)
          ? allEntityFields.find((el) => el.name === getParentField(col.name))
          : col,
        dependantField: isDependantField(col.name)
          ? col
          : allEntityFields.find(
              (el) => el.name === getDependantField(col.name)
            )
      };
    }
    return null;
  };

  const [saveTicket, { loading: submitActionLoading }] = useMutation(
    SAVE_TICKET,
    {
      onCompleted({ saveTicket }) {
        /* Close sheet container once an employee created */
        if (saveTicket) {
          toast('success', 'app.toast.newTicket.creation.success');
          useActivityTracker({ event: 'Ticket - Created a ticket' });
        }
      }
    }
  );

  const onCreateTicket = (res) => {
    if (res?.data?.saveTicket) {
      if (source === 'CHAT' && flags?.agentChatFe && isChatAvailable) {
        sessionStorage.setItem('OpenChat', true);
      }

      const params = {
        module: 'TICKET',
        data: {
          subject: res?.data?.saveTicket?.subject,
          displayId: res?.data?.saveTicket?.displayId,
          ticketId: res?.data?.saveTicket?.ticketId
        },
        ticketId: res?.data?.saveTicket?.ticketId
      };

      if (noRedirect) {
        tabContext.openTab(params);
      } else {
        tabContext.closeAndOpenTab(null, params);
      }
    }
  };

  const createTicket = (trackEvent = null) => {
    if (trackEvent) {
      useActivityTracker(trackEvent);
    }
    const isAgentVersionUpdated =
      currentUser?.rmmAppProperties?.betaAgentBasedFeatures?.agentChat;

    setErrors({});
    setConfirmDeflectionModalOpen(false);

    const inputParams = {
      ...ticket
    };
    // send deparment id only for IT product
    if (!isMSPProduct) {
      inputParams.department = ticket?.department?.businessFunctionId
        ? {
            businessFunctionId: ticket?.department?.businessFunctionId
          }
        : null;
    } else {
      // temp fix to remove department field for MSP product TODO: itsm
      delete inputParams.department;
    }

    if (template?.content || !isEmpty(template?.attachments)) {
      inputParams.conversation = {
        content: template?.content || null,
        attachments: template?.attachments?.map(
          ({ fileName, originalFileName, fileSize }) => {
            return { fileName, originalFileName, fileSize };
          }
        ),
        toUsers: [{ email: currentUser.email }]
      };
    }

    if (associateTimer && timerRef.current?.getTimerInputObj) {
      inputParams.updateWorklogTimer = timerRef.current?.getTimerInputObj;
    }

    if (selectedFormTemplate?.formId) {
      inputParams.form = {
        formId: selectedFormTemplate?.formId
      };
      const sopId =
        selectedFormTemplate?.standardOperatingProcedure?.sopId ||
        selectedFormTemplate?.runbook?.sopId;
      if (sopId) {
        inputParams.sop = {
          sopId: sopId
        };
      }
    }
    const updatedParams = removePropertiesForItProduct(
      inputParams,
      'saveTicket',
      currentProduct
    );
    const { addAssets = [], ...inputParamsNew } = updatedParams;
    const addAsset = addAssets?.map((el) => {
      return {
        assetId: el.assetId
      };
    });

    if (flags?.agentChatFe && isAgentVersionUpdated) {
      const additionalRequesterInput = inputParams?.addAdditionalRequester
        ?.length
        ? inputParams.addAdditionalRequester.map(({ userId }) => ({ userId }))
        : null;

      inputParamsNew.addAdditionalRequester = additionalRequesterInput;

      saveTicket({
        variables: {
          ...inputParamsNew,
          addAssets: addAsset,
          isMSPProduct: isMSPProduct
        },
        input: {
          ...inputParamsNew,
          addAssets: addAsset
        }
      }).then((res) => {
        onCreateTicket(res);
      });
    } else {
      const additionalRequesterInput = updatedParams?.addAdditionalRequester
        ?.length
        ? updatedParams.addAdditionalRequester.map(({ userId }) => ({
            userId
          }))
        : null;

      updatedParams.addAdditionalRequester = additionalRequesterInput;

      saveTicket({
        variables: { ...updatedParams, isMSPProduct: isMSPProduct },
        input: { ...updatedParams }
      }).then((res) => {
        onCreateTicket(res);
      });
    }
  };

  const [ticketDeflectionLoading, setTicketDeflectionLoading] = useState(false);

  const createTicketFromDeflection = () => {
    createTicket();
    setTicketDeflectionLoading(false);
  };

  const getDeflectionSuggestions = async (params) => {
    setErrors({});
    setTicketDeflectionLoading(true);
    try {
      const ticketDeflectionData = await MLClient.query({
        query: GENERATE_TICKET_DEFLECTION_SUGGESTIONS_V2,
        variables: params,
        fetchPolicy: 'no-cache'
      });
      const articleData =
        ticketDeflectionData?.data?.generateTicketDeflectionSuggestionsV2
          ?.aiGeneratedArticle;
      if (articleData) {
        setSuggestions(articleData);
        setConfirmDeflectionModalOpen(true);
        useActivityTracker({
          event: 'KB_ARTICLE_TICKET_DEFLECTION',
          data: {
            stepName: 'TICKET_DEFLECTION_SUGGESTIONS_POPUP'
          }
        });
      } else {
        createTicketFromDeflection();
      }
    } catch (error) {
      createTicketFromDeflection();
    } finally {
      setTicketDeflectionLoading(false);
    }
  };

  const save = () => {
    const formattedTicketData = { ...ticket };
    const form = selectedFormTemplate || null;
    const currentFormLayout = form?.fieldDetails
      ? JSON.parse(form?.fieldDetails)?.layout
      : [];

    const allFields = currentFormLayout.map((el) => ({
      name: el?.field?.fieldProps?.label,
      columnName: el?.field?.fieldProps?.name,
      type: el?.field?.type
    }));

    const mandatoryFieldsOnCurrentForm = allFields.filter((el) =>
      mandatoryFieldsOnCreation.some((f) => f?.columnName === el?.columnName)
    );

    let ticketConstraints = { ...newTicketConstraints };
    if (isMSPProduct) {
      ticketConstraints = {
        ...ticketConstraints,
        'client.accountId': {
          presence: {
            allowEmpty: false,
            message: '^Client must be selected'
          }
        }
      };
    } else {
      ticketConstraints = {
        ...ticketConstraints,
        'site.id': {
          presence: {
            allowEmpty: false,
            message: '^Site must be selected'
          }
        }
      };
    }

    mandatoryFieldsOnCurrentForm.forEach((el) => {
      const type = el?.type;
      const key =
        type === 'default' ? el.columnName : `customFields.${el.columnName}`;
      const newConstraint = {
        [key]: {
          presence: {
            allowEmpty: false,
            message: `^${el?.name} cannot be empty`
          }
        }
      };
      ticketConstraints = { ...ticketConstraints, ...newConstraint };
    });

    /* ask backend to change id to ticketId */

    formattedTicketData.description = dompurifier(template?.content);
    if (
      (selectedFormTemplate &&
        selectedFormTemplate?.standardOperatingProcedure?.sopId) ||
      selectedFormTemplate?.runbook?.sopId
    ) {
      formattedTicketData.sop = {
        sopId:
          selectedFormTemplate?.runbook?.sopId ||
          selectedFormTemplate?.standardOperatingProcedure?.sopId
      };
    }
    const formToValidate = cloneDeep(formattedTicketData);
    const validationResult = validateForm(formToValidate, ticketConstraints);
    if (validationResult) {
      setErrors(validationResult);
      return;
    }

    const getIsTicketDeflectionEnabledForClient = () => {
      if (ticketDeflectionClientConfigType === 'ALL') {
        return true;
      }
      return ticketDeflectionClientList?.find(
        (item) => item?.accountId === ticket?.client?.accountId
      );
    };
    const isTicketDeflectionEnabledForClient = getIsTicketDeflectionEnabledForClient();

    // Check if AI ticket deflection is enabled
    const isAiTicketDeflectionEnabled =
      isClient &&
      isKnowledgeBaseAvailable &&
      isTicketDeflectionActive &&
      isTicketDeflectionEnabledForClient;

    if (isAiTicketDeflectionEnabled) {
      // Call the API to get deflection suggestions if feature is enabled
      getDeflectionSuggestions({
        subject: ticket?.subject,
        description: ticket?.description
      });
    } else {
      // Skip deflection suggestions and create ticket directly
      createTicket();
    }
  };

  // Method to handle actual ticket creation after confirmation

  const closeDeflectionModal = (trackEvent) => {
    if (trackEvent) {
      useActivityTracker(trackEvent);
    }
    setConfirmDeflectionModalOpen(false);
    // Use TicketContext to navigate to the Tickets tab
    if (tabContext && tabContext.openTab) {
      // Navigate to the Tickets workspace
      tabContext.closeAndOpenTab(null, {
        id: 'workspace',
        content: '',
        module: 'WORKSPACE',
        tabId: 'workspace',
        state: {},
        data: {
          isClientPortal: true,
          type: 'TICKETWORKSPACE'
        },
        url: '/ticket/1/ticket-list'
      });
    }
  };

  const editorRef = React.useRef(null);
  const cancel = () => {
    const activeTab = tabContext?.activeTab;
    tabContext?.closeTab(activeTab);
  };

  const setEditorReference = (ref) => {
    editorRef.current = ref;
  };

  const getDefaultTicketForm = (res) => {
    const initalFormTemplate = res?.getDefaultTicketForm;
    if (initalFormTemplate) {
      setSelectedFormTemplate({
        ...initalFormTemplate,
        name: initalFormTemplate?.formName,
        label: initalFormTemplate?.formName,
        value: initalFormTemplate?.formName
      });
    }
  };

  const [getTicketForm, { loading }] = useLazyQuery(defaultFormQuery, {
    onCompleted: getDefaultTicketForm
  });

  const getTicketDeflectionData = () => {
    if (isMSPProduct && !isMSP) {
      AccountClient.query({
        query: GET_AI_FEATURES_LIST_INDIVIDUAL,
        variables: {
          input: {
            listInfo: {
              condition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'featureId',
                    operator: 'is',
                    value: 12
                  }
                ]
              }
            }
          }
        },
        fetchPolicy: 'network-only'
      })
        .then((response) => {
          if (response?.data?.getAIFeatureList) {
            const feature = response.data.getAIFeatureList?.feature;
            const ticketDeflectionFeatureData = feature[0];
            if (ticketDeflectionFeatureData) {
              const configType = ticketDeflectionFeatureData?.clientConfigType;
              setTicketDeflectionClientConfigType(configType);
              if (configType !== 'ALL') {
                setTicketDeflectionClientList(
                  ticketDeflectionFeatureData?.clients
                );
              }
            }
          }
        })
        .catch((error) => {
          // console.error('Error fetching ticket deflection:', error);
        });
    }
  };

  const handleTemplateChange = (val) => {
    const formattedLayout = formatFormTemplate(val?.fieldDetails, true);
    const updatedFormTemplate = {
      ...val,
      fieldDetails: formattedLayout
    };
    if (errors) {
      setErrors(null);
    }
    setSelectedFormTemplate(val ? updatedFormTemplate : null);
  };

  const updateSelectedFormTemplate = (updatedTemplate) => {
    setSelectedFormTemplate(updatedTemplate);
  };

  const toggleScheduleModal = () => {
    const formToValidate = cloneDeep(ticket);
    const validationResult = validateForm(formToValidate, newTicketConstraints);
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setErrors(null);
    setScheduleModalOpen(!scheduleModalOpen);
  };

  const getNameFromObj = (userObj) => {
    const { name, firstName, lastName, email } = userObj;
    if (firstName) {
      if (lastName) {
        return `${firstName} ${lastName}`;
      }
      return firstName;
    }
    if (name) {
      return name;
    }
    return email || '';
  };

  const toggleSheetActive = () => {
    setSheetActive((prevState) => !prevState);
  };

  const addedAssets = (selectedAssets) => {
    const currentAssets = ticket?.addAssets ?? [];

    if (selectedAssets.addAssets) {
      const selectedAssetsData = selectedAssets.addAssets.map((assetData) => {
        return pick(assetData, [
          'assetId',
          'name',
          'assetClass.classId',
          'assetClass.name'
        ]);
      });
      updateTicketProperty({
        addAssets: [...currentAssets, ...selectedAssetsData]
      });
    }

    if (selectedAssets.deleteAssets) {
      // Filter the assets array to exclude the assets to be deleted
      const updatedAssets = ticket?.addAssets?.filter(
        (asset) =>
          !selectedAssets.deleteAssets
            .map((deleteAsset) => deleteAsset.assetId)
            .includes(asset.assetId)
      );
      updateTicketProperty({
        addAssets: [...updatedAssets]
      });
    }
  };

  const isMSPClientProduct = isMSPClient;

  const updateSite = (val, resetUser = false) => {
    let additionalData = {
      additionalFields: true,
      addAdditionalRequester: null
    };
    const updatedSite = val?.id
      ? {
          id: val.id,
          name: val.name
        }
      : null;
    if (resetUser) {
      additionalData = {
        ...additionalData,
        requester: null
      };
    }
    updateField('site', updatedSite, additionalData);
  };

  const isScheduleAuthorised = useIsAuthorisedHook(`ticketScheduler.add`);

  const getDefaultTicketData = () => {
    const ticketDataFromAssets = getTicketDataFromAssets();
    if (ticketDataFromAssets) {
      return ticketDataFromAssets;
    }
    return {
      client: getDefaultClient(),
      requester: isClient ? { userId: userId } : null,
      technician: getDefaultTechnician()
    };
  };

  const defaultTicketData = getDefaultTicketData();

  if (!isMSPProduct) {
    defaultTicketData.department = userDepartment || null;
    if (isClient) {
      const clientId = defaultTicketData?.client?.accountId;
      const defultSiteData = getSiteByOrgAccountId(clientId)?.[0];
      if (defultSiteData?.name) {
        defaultTicketData.site = defultSiteData;
      }
    }
  }

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      setSource(tabContext?.activeTab?.data?.source);
      setTicket(defaultTicketData);
      getTicketDeflectionData();
      if (!isEmpty(defaultTicketData?.addAdditionalRequester)) {
        setShowAdditionalRequester(true);
      }
      getTicketForm();
    }
    if (initialized && isMSP && isEmpty(ticket?.client)) {
      const clientFromTab = getClientFromTab();
      if (clientFromTab) {
        const updatedTicket = {
          ...ticket,
          client: clientFromTab
        };
        setTicket(updatedTicket);
      }
    }

    EventEmitter.subscribe('imageUploading', setImageUploading);

    return function cleanup() {
      EventEmitter.unsubscribe('imageUploading');
    };
  }, []);

  if (!ticket || entitiesLoading) {
    return (
      <>
        <Loader />
      </>
    );
  }

  return (
    <div className="p16 tab-page full-height fx g20 white-grey-page mauto mw1400">
      <div className="fg1">
        <Page
          variant="secondary"
          divider
          contentHeight="80px"
          title={
            <div className="fx center">
              <Heading element="h1">
                <FormattedMessage id="app.create.ticket.new" />
              </Heading>
              {!checkClient && (
                <div className="ml20 mt5 fx center">
                  <FeatureGuide featureName="creatingticket" video onlyIcon />
                </div>
              )}
            </div>
          }
          primaryAction={
            <ButtonGroup>
              <Button id="cancelTicketButton" plainSecondary onClick={cancel}>
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button
                id="saveNewTicket"
                loading={submitActionLoading || ticketDeflectionLoading}
                primary
                onClick={save}
                disabled={imageUploading}
                icon={<SVGIcon name="Save" color="#ffffff" />}
                connectedDisclosure={
                  isScheduleAuthorised &&
                  !associateTimer && {
                    disabled: imageUploading,
                    actions: [
                      {
                        content: (
                          <div className="fx" onClick={toggleScheduleModal}>
                            <SVGIcon name="DateTime" />
                            <span className="ml-10">
                              <FormattedMessage id="admin.ticketScheduler.create" />
                            </span>
                          </div>
                        )
                      }
                    ]
                  }
                }
              >
                <span className="fx actr">
                  <FormattedMessage id="common.save" />
                </span>
              </Button>
            </ButtonGroup>
          }
        >
          <TicketScheduleModal
            ticketData={{
              ticket: ticket,
              template: {
                content: template?.content || '',
                attachments: template?.attachments || []
              },
              selectedFormTemplate: selectedFormTemplate,
              editMode: false
            }}
            scheduleModalOpen={scheduleModalOpen}
            toggleScheduleModal={toggleScheduleModal}
          />

          <Wrapper>
            <div className="flex source g20 mt20 col pl20">
              <div className="flex actr pl20 pr30 g20 w100">
                <AccessControl
                  roleToCheck="client.view"
                  productToCheck="MSP"
                  render={
                    <div className="fx w100 wm-215">
                      <ClientSelect
                        fetchBefore
                        prePopulateOptions={checkClient && clientList}
                        updateField={updateField}
                        property="accountId"
                        value="client"
                        selectedValue={ticket?.client}
                        conditions={null}
                        query={GET_CLIENTS_FOR_DROPDOWN}
                        defaultValue={
                          ticket?.client?.name
                            ? {
                                name: ticket?.client?.name,
                                label: ticket?.client?.name,
                                value: ticket?.client?.name
                              }
                            : null
                        }
                        errors={errors?.['client.accountId']?.[0]}
                        fullWidth
                      />
                    </div>
                  }
                />
                <AccessControl
                  roleToCheck={{
                    operator: 'OR',
                    roles: ['site.view', 'companySite.view']
                  }}
                  render={
                    <div className="w100 wm-215">
                      <SiteSelect
                        accountId={ticket?.client?.accountId}
                        prePopulateOptions={
                          checkClient && getSiteByOrgAccountId()
                        }
                        isMSPClientProduct={!!isMSPClientProduct}
                        // If isMSPClientProduct we'll populate the data in useEffect
                        // so we'll auto populate first value based on initialized also
                        ignoreAutoPopulateFirstValue={!!ticket?.site}
                        updateSite={updateSite}
                        site={ticket?.site}
                        errors={errors?.['site.id']?.[0]}
                        hideLabel
                        icon
                        roundedIcon
                        fullWidth
                        disableClearable
                      />
                    </div>
                  }
                />
                <AccessControl
                  productToCheck={COMMON_CONSTANTS.IT}
                  roleToCheck="department.view"
                  render={
                    <div className="w100 wm-215">
                      <DepartmentSelect
                        onChange={(val) => {
                          if (val) {
                            updateField(
                              'department',
                              {
                                businessFunctionId: val.value,
                                name: val.name
                              },
                              {
                                additionalFields: true
                              }
                            );
                          } else {
                            updateField('department', null);
                          }
                        }}
                        department={ticket?.department}
                        showIcon
                      />
                    </div>
                  }
                />
                <div className="w100 wm-215">
                  <TicketTypeContainer
                    currentValue={ticket?.ticketType}
                    onChange={updateField}
                    fullWidth
                  />
                </div>

                <div className="w100 wm-215">
                  <div className="formTemplates flex center select-container w100">
                    <Badge size="small" radial status="success">
                      <div className="opacity6 flex">
                        <SVGIcon name="Form" />
                      </div>
                    </Badge>
                    <div className="full-width ml8">
                      <Dropdown
                        noBorder
                        classNamePrefix="value_selector"
                        isClearable
                        isLoading={formsLoading}
                        value={selectedFormTemplate}
                        onChange={handleTemplateChange}
                        placeholder={getTranslatedText(
                          intl,
                          'ticket.new.pl.temp'
                        )}
                        name="forms"
                        options={ticketFormOptions}
                        onMenuOpen={getTicketForms}
                        noOptionsMessage={() => {
                          return formsLoading ? 'Loading...' : 'No options';
                        }}
                        formatGroupLabel={formatGroupLabel}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        closeMenuOnScroll={(e) => {
                          return (
                            e.target &&
                            e.target.classList &&
                            !(
                              e.target.classList.value.indexOf('menu-list') > -1
                            )
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`flex actr w100 pl20 pr30 ${
                  isMSPProduct ? 'g20' : 'g25'
                }`}
              >
                <AccessControl
                  roleToCheck={{
                    operator: 'OR',
                    roles: ['users.view', 'employees.view']
                  }}
                  render={
                    <div className="w100 wm-215">
                      <UserSelect
                        fetchBefore
                        noMargin
                        createRequester={flags?.creareRequesterFromTicket}
                        client={ticket?.client}
                        updateField={(key, value, additionalProps) => {
                          // auto update department field - only for IT product
                          const departmentProps = additionalProps
                            ? {
                                department: additionalProps.businessFunction
                              }
                            : null;
                          updateField(key, value, {
                            additionalFields: !isMSPProduct,
                            ...departmentProps
                          });
                        }}
                        property="userId"
                        placeholder={
                          !isMSPProduct &&
                          getTranslatedText(intl, 'itsm.choose.user')
                        }
                        field="requester"
                        value={ticket?.requester}
                        defaultValue={ticket?.requester}
                        selectedValue={ticket?.requester}
                        errors={errors?.['requester.userId']?.[0]}
                        sortList
                        showEmail
                        site={ticket?.site}
                        department={ticket?.department}
                        showInitials
                        dropDownComponents={{
                          IndicatorSeparator
                        }}
                      />
                    </div>
                  }
                  fallback={
                    isMSP ? null : (
                      <div className="fx center mr-20">
                        <Avatar size="small" name="requesters" style="two" />
                        <div className="ml-10 mr-10">
                          {getNameFromObj(currentUser)}
                        </div>
                      </div>
                    )
                  }
                />

                <AccessControl
                  roleToCheck="employees.view"
                  render={
                    <div className="w100 wm-215">
                      <TechnicianCell
                        entitiesMeta={entitiesMeta}
                        ticket={ticket}
                        updateProperties={updateProperties}
                        getCellProps={getCellProps}
                      />
                    </div>
                  }
                />
              </div>

              <AccessControl
                roleToCheck="client.view"
                productToCheck="MSP"
                render={
                  <div className="flex w100 pl20 pr30 g20">
                    <div className="w100 wm-215 mr-5">
                      {showAdditionalRequester ? (
                        <>
                          <UserSelect
                            noMargin
                            isMulti
                            fetchBefore
                            client={ticket?.client}
                            updateField={(key, value, additionalProps) => {
                              const usersArr = !isEmpty(additionalProps)
                                ? Object.values(additionalProps)
                                : null;
                              if (usersArr) {
                                const formattedUsers = usersArr?.map(
                                  ({
                                    name,
                                    label,
                                    value,
                                    userId,
                                    firstName,
                                    lastName,
                                    email
                                  }) => ({
                                    name,
                                    label,
                                    value,
                                    userId,
                                    firstName,
                                    lastName,
                                    email
                                  })
                                );
                                updateField(
                                  'addAdditionalRequester',
                                  formattedUsers || null
                                );
                              } else {
                                updateField('addAdditionalRequester', null);
                              }
                            }}
                            property="userId"
                            placeholder={getTranslatedText(
                              intl,
                              'placeholder.ticket.additionalRequester'
                            )}
                            field="addAdditionalRequester"
                            sortList
                            site={ticket?.site}
                            department={ticket?.department}
                            allowAdditionalProps
                            defaultMenuIsOpen={
                              !!ticket?.client?.accountId &&
                              isEmpty(ticket?.addAdditionalRequester)
                            }
                            selectedValue={ticket?.addAdditionalRequester || []}
                            value={ticket?.addAdditionalRequester || []}
                            defaultValue={ticket?.addAdditionalRequester || []}
                            dropDownComponents={{
                              IndicatorSeparator
                            }}
                            preventSiteCondition
                          />
                        </>
                      ) : (
                        <Button
                          icon={<SVGIcon name="Add" />}
                          plain
                          onClick={() => {
                            setShowAdditionalRequester(true);
                          }}
                        >
                          <FormattedMessage id="placeholder.ticket.additionalRequester" />
                        </Button>
                      )}
                    </div>
                  </div>
                }
              />
            </div>

            <TicketForm
              ticket={ticket}
              updateField={updateField}
              errors={errors}
              selectedFormTemplate={selectedFormTemplate}
              updateSelectedFormTemplate={updateSelectedFormTemplate}
              mandatoryFieldsOnCreation={mandatoryFieldsOnCreation}
              setMandatoryFieldsOnCreation={setMandatoryFieldsOnCreation}
              editorConfig={superEditorConfig}
              isSuperEditor
            />
          </Wrapper>
        </Page>
        <Modal
          open={confirmDeflectionModalOpen}
          title={getTranslatedText(intl, 'ai.ticket.deflection.v2.title')}
          showCloseButton={false}
          large
          secondaryActions={{
            content: (
              <Button icon={<SVGIcon name="ThumbsDownOutline" />}>
                <span className="fx actr">
                  <TextStyle>
                    {getTranslatedText(
                      intl,
                      'ai.ticket.deflection.v2.nothelpful.btn'
                    )}
                  </TextStyle>
                </span>
              </Button>
            ),
            onAction: () =>
              createTicket({
                event: 'KB_ARTICLE_TICKET_DEFLECTION',
                data: {
                  stepName: 'SUGGESTION_NOT_HELPFUL'
                }
              }),
            loading: submitActionLoading
          }}
          primaryAction={{
            icon: <SVGIcon name="LikeThumb" />,
            content: getTranslatedText(
              intl,
              'ai.ticket.deflection.v2.helpful.btn'
            ),
            onAction: () =>
              closeDeflectionModal({
                event: 'KB_ARTICLE_TICKET_DEFLECTION',
                data: {
                  stepName: 'SUGGESTION_HELPFUL'
                }
              })
          }}
          dialogClassName="kb-relevant-article-wrapper"
        >
          <Modal.Section>
            <KbRelevantArticle suggestions={state.suggestions} />
          </Modal.Section>
        </Modal>
      </div>
      <div className="fx col g15">
        {associateTimer && (
          <div className="fw min-width-350 maxW450">
            <WorklogHoursSection
              editMode
              hideCancel
              hideDelete
              noBoxShadow
              highlightRunningTimer
              timer={associateTimer}
              forwardedRef={timerRef}
              workItem={associateTimer?.workItem}
              module={associateTimer?.module || null}
              onlyManual={associateTimer?.type !== 'AUTOMATIC'}
            />
          </div>
        )}
        {flags?.agentChatFe && isAssetGatingAvailable && isAssetAuthorised && (
          <div className="fw min-width-350 wm-450">
            <Card padding="sizeSmall">
              <div className="flex fx-sb actr w100 mb10">
                <div>
                  <TextStyle weight="SemiBold">
                    <FormattedMessage id="ticket.details.insights.assets" />
                  </TextStyle>
                </div>
                <div>
                  <AccessControl
                    roleToCheck={{
                      operator: 'AND',
                      roles: ['tickets.edit', 'asset.view']
                    }}
                    render={
                      <div className="mlauto">
                        <Button
                          onClick={toggleSheetActive}
                          size="tiny"
                          plain
                          monochrome
                          icon={<SVGIcon name="Add" />}
                        />
                      </div>
                    }
                  />
                  {sheetActive && (
                    <TicketAssetsSheet
                      toggleSheetActive={toggleSheetActive}
                      // refetchAssets={refetchAssets}
                      ticketAssets={ticket?.addAssets}
                      addedAssets={addedAssets}
                      ticket={ticket}
                      open={sheetActive}
                      newTicket
                    />
                  )}
                </div>
              </div>
              <div className="max-height-150 overflow-y-scroll x-hidden">
                {ticket?.addAssets?.length > 0 ? (
                  ticket?.addAssets.map((el) => {
                    return (
                      <div className="flex asset actr hiddenContainer lh-40">
                        <AssetTypeCell cell={{ row: { original: el } }} />
                        <div
                          className="ellipsis ml8"
                          onClick={() => {
                            if (isAssetAuthorised) {
                              tabContext.openTab({
                                module: 'ASSET_DETAILS',
                                data: {
                                  id: el.assetId,
                                  classId: el?.assetClass?.classId,
                                  subject: el?.name
                                }
                              });
                            }
                          }}
                        >
                          <TextStyle weight="Medium">{el?.name}</TextStyle>
                        </div>
                        <div className="mlauto flex-row flex-align-center">
                          <AccessControl
                            roleToCheck="tickets.edit"
                            render={
                              <div className="hiddenActions">
                                <Button
                                  onClick={() => {
                                    addedAssets({
                                      deleteAssets: [el]
                                    });
                                  }}
                                  icon={
                                    <SVGIcon
                                      size="sm"
                                      name="RejectedCircle"
                                      color="var(--sops-black-white-bg)"
                                    />
                                  }
                                />
                              </div>
                            }
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div>
                      <FormattedMessage id="ticket.details.insights.assets.noAssets" />
                    </div>
                  </>
                )}
              </div>
              {isAssetAuthorised && (
                <div className="mt10">
                  <div className="mb10">
                    <TextStyle weight="SemiBold">
                      <FormattedMessage id="ticket.details.insights.requesterassets" />
                    </TextStyle>
                  </div>
                  <div>
                    <AssociateAssets
                      assets={ticket?.addAssets}
                      addedAssets={addedAssets}
                      ticket={ticket}
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketSheetNew;
